import { DataPlatformApi } from '@kcftech/data-platform-sdk';
import {
    IndicatorAlertStatusResponse,
    MetricTypeCodes,
    SensorRoleTypeCodes,
} from '@kcftech/prometheus-client';
import { ONE_MINUTE_MS } from 'configs/constants';
import { dataPlatformClient, sdClient } from 'contexts/SDKClientContext';
import { getUnitsRequest } from 'custom_hooks/data_hooks/burst_data/useBurstData';
import {
    BaseDataSourceOptions,
    DataSourceQuery,
} from 'custom_hooks/data_hooks/data_source/data_source';
import { useInfiniteUnitsDataSource } from 'custom_hooks/data_hooks/data_source/unitDataSource';
import { useDrainDataSource } from 'custom_hooks/data_hooks/data_source/useDrainDataSource';
import {
    InfiniteDataSource,
    useInfiniteDataSource,
} from 'custom_hooks/data_hooks/data_source/useInfiniteDataSource';
import {
    PagedDataSource,
    usePagedDataSource,
} from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import {
    ensureIndicatorFilters,
    ensureIndicatorFiltersByIds,
} from 'custom_hooks/data_hooks/indicator/ensureIndicatorFilters';
import { mapDataPlatformIndicatorToGeneralIndicatorInfo } from 'custom_hooks/data_hooks/indicator/shared';
import { useCurrentUserInfo } from 'custom_hooks/data_hooks/user/useCurrentUserInfo';
import { IndicatorType } from 'pages/workbench/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
    excludeNullAndUndefined,
    isDefinedAndNotEmpty,
    isUndefinedOrEmpty,
    toMap,
} from 'utils/array';
import { buildDataPlatformOrderBy, toDataPlatformDataSourceResult } from 'utils/query_v3';
import { sentryCaptureException } from 'utils/sentry';

export type DataPlatformIndicatorFilter = Readonly<{
    groupIds?: string[];
    customFilterIds?: string[];
    indicatorTypes?: IndicatorType[];
    sensorRoleTypes?: (SensorRoleTypeCodes | null)[];
    filtered?: boolean;
    multirateLow?: boolean;
    showHistoricalIndicators?: boolean;
    orientations?: (DataPlatformApi.Orientation | null)[];
    dataTypes?: string[];
    calculations?: string[];
    bands?: (DataPlatformApi.IndicatorBandCode | null)[];
    labels?: string[];
    nameSearch?: string;
}>;

export type DataPlatformIndicatorDataSourceOptions = BaseDataSourceOptions<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

export type ResolvedMultirateLowFilter =
    | Readonly<{ kind: 'no-match' }>
    | Readonly<{ kind: 'filter'; value: boolean }>
    | Readonly<{ kind: 'no-filter' }>;

export function resolveMultirateLowFilter(
    collectionFilter: boolean[],
): ResolvedMultirateLowFilter {
    if (collectionFilter.length === 0) {
        return { kind: 'no-match' };
    }
    if (collectionFilter.length === 1) {
        return { kind: 'filter', value: collectionFilter[0] };
    }
    return { kind: 'no-filter' };
}

export function getMultirateLowFilterValue(
    collectionFilter: boolean[],
): boolean | undefined {
    const resolved = resolveMultirateLowFilter(collectionFilter);
    return resolved.kind === 'filter' ? resolved.value : undefined;
}

const getIndicatorFilterNameForIndicatorType = (
    indicatorType: IndicatorType,
): string | undefined => {
    switch (indicatorType) {
        case MetricTypeCodes.VibrationOverallPeak:
            return 'Peak Velocity';
        case MetricTypeCodes.VibrationOverallPeakAccel:
            return 'Peak Acceleration';
        case MetricTypeCodes.VibrationOverallRms:
            return 'RMS Velocity';
        case MetricTypeCodes.VibrationOverallRmsAccel:
            return 'RMS Acceleration';
        case MetricTypeCodes.VibrationOverall:
            return 'Overall Vibration';
        case MetricTypeCodes.Voltage:
            return 'Voltage';
        case MetricTypeCodes.Temperature:
            return 'Temperature';
        case MetricTypeCodes.SignalStrength:
            return 'Signal Strength';
        case MetricTypeCodes.VibrationBandMax:
        case MetricTypeCodes.VibrationBandRms:
        case MetricTypeCodes.VibrationBandMaxAccel:
        case MetricTypeCodes.VibrationBandRmsAccel:
            return 'Vibration Bands';
        case 'ultrasound':
            return 'Ultrasound';
        default:
            return undefined;
    }
};

export const mapSensorRoleTypeToAxis = (
    sensorRoleType: SensorRoleTypeCodes | null,
): DataPlatformApi.Axis | null => {
    switch (sensorRoleType) {
        case SensorRoleTypeCodes.VibrationXAxis:
            return DataPlatformApi.Axis.X;
        case SensorRoleTypeCodes.VibrationYAxis:
            return DataPlatformApi.Axis.Y;
        case SensorRoleTypeCodes.VibrationZAxis:
            return DataPlatformApi.Axis.Z;
        case null:
            return null;
        default:
            throw new Error('Attempted to filter for an unsupported sensor role type');
    }
};

const mapFiltersOntoIndicatorSpec = (
    filter: DataPlatformIndicatorFilter,
    axes: (DataPlatformApi.Axis | null)[] | undefined,
    indicatorSpec: DataPlatformApi.IndicatorSpec,
): DataPlatformApi.IndicatorSpec[] => {
    const { orientations, filtered, multirateLow } = filter;
    const baseSpec = {
        ...indicatorSpec,
        ...(filtered !== undefined && { filtered }),
        ...(multirateLow !== undefined && { multirateLow }),
    };

    // If no axes are provided, create a spec for each orientation
    if (isUndefinedOrEmpty(axes)) {
        // If no orientations are provided, create a single spec without axis or orientation
        if (isUndefinedOrEmpty(orientations)) {
            return [baseSpec];
        }

        return orientations.map((orientation) => ({ ...baseSpec, orientation }));
    }

    // If no orientations are provided, create a spec for each axis
    if (isUndefinedOrEmpty(orientations)) {
        return axes.map((axis) => ({ ...baseSpec, axis }));
    }

    // Else, map a spec for each axis and orientation combination
    return axes.flatMap((axis) =>
        orientations.map((orientation) => ({ ...baseSpec, axis, orientation })),
    );
};

function collapse<T>(arr: T[] | undefined): (T | undefined)[] {
    return isUndefinedOrEmpty(arr) ? [undefined] : arr;
}

function generateIndicatorSpecsForAllFilterProps(
    filter: DataPlatformIndicatorFilter,
    axes: (DataPlatformApi.Axis | null)[] | undefined,
): DataPlatformApi.IndicatorSpec[] {
    const specs: DataPlatformApi.IndicatorSpec[] = [];
    // axes and orientations are included on everything if they exist
    //
    // on the other hand, these:
    // // dataTypes, calculations, band, labels
    // combine with each other via OR
    for (const dataType of collapse(filter.dataTypes)) {
        for (const calculation of collapse(filter.calculations)) {
            for (const band of collapse(filter.bands)) {
                for (const label of collapse(filter.labels)) {
                    specs.push(
                        ...mapFiltersOntoIndicatorSpec(filter, axes, {
                            physicalQuantity: dataType,
                            statistic: calculation,
                            band,
                            label,
                        }),
                    );
                }
            }
        }
    }

    return specs;
}

const getIndicatorSpecsForOtherFilters = (
    filter: DataPlatformIndicatorFilter,
    axes: (DataPlatformApi.Axis | null)[] | undefined,
): DataPlatformApi.IndicatorSpec[] => {
    const indicatorSpecs: DataPlatformApi.IndicatorSpec[] = [];

    const hasAnyOtherFilters =
        isDefinedAndNotEmpty(filter.dataTypes) ||
        isDefinedAndNotEmpty(filter.calculations) ||
        isDefinedAndNotEmpty(filter.bands) ||
        isDefinedAndNotEmpty(filter.labels);

    if (hasAnyOtherFilters) {
        // Combination of any/all 4 filters: dataTypes, calculations, bands, labels
        indicatorSpecs.push(...generateIndicatorSpecsForAllFilterProps(filter, axes));
    }

    // If no other filters are provided, create a spec with only the axes and orientations
    if (
        !hasAnyOtherFilters &&
        isUndefinedOrEmpty(filter.indicatorTypes) &&
        (isDefinedAndNotEmpty(axes) ||
            isDefinedAndNotEmpty(filter.orientations) ||
            filter.filtered !== undefined ||
            filter.multirateLow !== undefined)
    ) {
        indicatorSpecs.push(...mapFiltersOntoIndicatorSpec(filter, axes, {}));
    }

    return indicatorSpecs;
};

export const getIndicatorSpecs = (
    filter: DataPlatformIndicatorFilter,
    defaultFilters: DataPlatformApi.FilterResponse[],
) => {
    const axes = filter.sensorRoleTypes
        ?.filter(
            (srt) =>
                srt === SensorRoleTypeCodes.VibrationXAxis ||
                srt === SensorRoleTypeCodes.VibrationYAxis ||
                srt === SensorRoleTypeCodes.VibrationZAxis ||
                srt === null,
        )
        .map(mapSensorRoleTypeToAxis);

    const specs = getIndicatorSpecsForOtherFilters(filter, axes);

    if (filter.indicatorTypes) {
        const uniqueIndicatorFilterNames = new Set(
            excludeNullAndUndefined(
                filter.indicatorTypes.map(getIndicatorFilterNameForIndicatorType),
            ),
        );

        const indicatorSpecs = defaultFilters
            .filter((filter) => uniqueIndicatorFilterNames.has(filter.name))
            .flatMap((filter) => filter.filter);

        const indicatorTypeSpecs = indicatorSpecs.flatMap((spec) =>
            mapFiltersOntoIndicatorSpec(filter, axes, spec),
        );

        specs.push(...indicatorTypeSpecs);
    }

    return specs;
};

export const getDataPlatformIndicators: DataSourceQuery<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
> = async ({ filter, sort, page, pageLimit }) => {
    const orderBy = buildDataPlatformOrderBy(sort);

    const defaultFilters = await ensureIndicatorFilters();
    const customFilters = await ensureIndicatorFiltersByIds(filter.customFilterIds ?? []);

    return toDataPlatformDataSourceResult(
        dataPlatformClient.indicators.getIndicators({
            groupIds: filter.groupIds ?? [],
            indicatorSpecs: [
                ...getIndicatorSpecs(filter, defaultFilters),
                ...customFilters.flatMap((filter) => filter.filter),
            ],
            limit: pageLimit,
            nameLike: filter.nameSearch === '' ? undefined : filter.nameSearch,
            includeInactive: filter.showHistoricalIndicators,
            offset: (page - 1) * pageLimit,
            sort: orderBy,
        }),
    );
};

export type IndicatorPagedDataPlatformDataSource = PagedDataSource<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

export function usePagedDataPlatformIndicatorDataSource(
    options: DataPlatformIndicatorDataSourceOptions,
): IndicatorPagedDataPlatformDataSource {
    return usePagedDataSource({
        ...options,
        query: getDataPlatformIndicators,
    });
}

export type IndicatorInfiniteDataPlatformDataSource = InfiniteDataSource<
    DataPlatformApi.IndicatorResponse,
    DataPlatformIndicatorFilter
>;

export function useInfiniteDataPlatformIndicatorDataSource(
    options: DataPlatformIndicatorDataSourceOptions,
): IndicatorInfiniteDataPlatformDataSource {
    return useInfiniteDataSource({
        defaultSort: undefined,
        ...options,
        query: getDataPlatformIndicators,
    });
}

export function useInfiniteEnrichedDataPlatformIndicatorDataSource(
    options: DataPlatformIndicatorDataSourceOptions,
) {
    const { results: units } = useDrainDataSource(
        useInfiniteUnitsDataSource({
            name: 'indicator-legend-units',
            defaultFilter: {},
        }),
    );

    const currentUser = useCurrentUserInfo();
    const unitsRequest = getUnitsRequest(currentUser.data);

    const unitCodeToUnit = useMemo(
        () => (units ? toMap(units, (unit) => unit.Code) : undefined),
        [units],
    );

    const dataSource = useInfiniteDataPlatformIndicatorDataSource(options);

    const [alertStatuses, setAlertStatuses] = useState<
        Record<string, IndicatorAlertStatusResponse>
    >({});
    const fetchedTimestampsRef = useRef<Record<string, number>>({});

    useEffect(() => {
        if (!dataSource.results || dataSource.results.length === 0) {
            return;
        }

        const now = Date.now();
        const currentTimestamps = fetchedTimestampsRef.current;

        const idsToFetch = dataSource.results
            .map((indicator) => indicator.id)
            .filter((id) => {
                const lastFetched = currentTimestamps[id];

                return !lastFetched || now - lastFetched > ONE_MINUTE_MS;
            });

        if (idsToFetch.length === 0) {
            return;
        }

        idsToFetch.forEach((id) => {
            currentTimestamps[id] = now;
        });

        const fetchChunkStatuses = async () => {
            try {
                const indicatorAlertStatuses = await sdClient.getIndicatorAlertStatus({
                    body: idsToFetch,
                });

                const statusUpdate: Record<string, IndicatorAlertStatusResponse> = {};
                indicatorAlertStatuses.forEach((status) => {
                    statusUpdate[status.DpIndicatorId] = status;
                });

                setAlertStatuses((prev) => ({
                    ...prev,
                    ...statusUpdate,
                }));
            } catch (error) {
                sentryCaptureException({
                    error,
                    tags: { hook: 'useInfiniteEnrichedDataPlatformIndicatorDataSource' },
                    context: {
                        fetchChunkStatuses: {
                            chunkSize: idsToFetch.length,
                        },
                    },
                });

                idsToFetch.forEach((id) => {
                    delete fetchedTimestampsRef.current[id];
                });
            }
        };

        fetchChunkStatuses();
    }, [dataSource.results]);

    const enrichedResults = useMemo(() => {
        if (!dataSource.results) {
            return undefined;
        }

        const indicators = dataSource.results.map((indicator) =>
            mapDataPlatformIndicatorToGeneralIndicatorInfo(
                indicator,
                unitsRequest,
                unitCodeToUnit,
            ),
        );

        return indicators.map((indicator) => {
            const statusMatch = alertStatuses[indicator.Id] as
                | IndicatorAlertStatusResponse
                | undefined;

            return statusMatch
                ? { ...indicator, AlertStatus: statusMatch.AlertStatus }
                : indicator;
        });
    }, [dataSource.results, unitsRequest, unitCodeToUnit, alertStatuses]);

    return {
        ...dataSource,
        results: enrichedResults,
    };
}
